import { useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import { useCallActions } from '#features/calls/hooks/useCallActions';
import { useCallStore } from '#features/calls/model/callStore';
import { leaveVoice } from '#features/voice/model/voice';
import { useVoiceStore } from '#features/voice/model/voiceStore';
import {
  onVoiceForegroundLeave,
  onVoiceForegroundToggleMic,
  startVoiceForeground,
  stopVoiceForeground,
  updateVoiceForeground,
  type VoiceForegroundContent,
} from '#features/voice/utils/nativeVoiceForeground';
import { requestVoiceMicrophone } from '#features/voice/utils/nativeVoiceSession';
import { envConfig } from '#constants/envConfig';

/**
 * Пока человек в голосе – в комнате сессии или в звонке 1:1, – Android-оболочка держит
 * foreground service с табличкой «ты в голосе» в шторке.
 *
 * Без него через минуту после сворачивания приложения или гашения экрана система глушит запись:
 * собеседник слышит тишину, а у всех на экране микрофон включён (замер на телефоне 14.09.2026).
 * Это ровно тот случай, ради которого голос и нужен: телефон лежит рядом, человек играет.
 *
 * Сервис поднимается уже на входе («connecting»), а не по готовности соединения: Android 12+
 * разрешает запуск только из переднего плана, и в момент нажатия «Войти» приложение на нём точно.
 * Разрыв с переподключением сервис не гасит – голос ещё жив.
 *
 * Кнопка «Выйти» в шторке зовёт обычный выход: сервис выключается уже им, когда соединения не
 * осталось, – так табличка не может сказать «вышел», пока человек всё ещё в голосе.
 *
 * В вебе не делает ничего. Монтировать один раз, высоко в дереве.
 */
/** Сколько раз и как часто пробовать поднять сервис после отказа, пока человек в голосе. */
const RETRY_LIMIT = 15;
const RETRY_DELAY_MS = 2_000;

export const useNativeVoiceForeground = () => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { hangup } = useCallActions();

  const voicePhase = useVoiceStore((s) => s.phase);
  const gameName = useVoiceStore((s) => s.room?.gameName ?? null);
  const micDenied = useVoiceStore((s) => s.micDenied);
  const voiceMicOn = useVoiceStore((s) => s.micEnabled);
  const callPhase = useCallStore((s) => s.phase);
  const peerName = useCallStore((s) => s.call?.peer.displayName ?? null);
  const callAcceptedAt = useCallStore((s) => s.call?.acceptedAt ?? null);
  const callMicOn = useCallStore((s) => s.micEnabled);

  // Звонок забирает микрофон у сессии (useVoiceCallArbitration), поэтому одновременно живёт
  // только один разговор, и табличка говорит о нём.
  const inCall = callPhase === 'outgoing' || callPhase === 'active';
  const inVoice =
    voicePhase === 'connecting' || voicePhase === 'connected' || voicePhase === 'reconnecting';

  /*
    Таймер голоса сессии идёт от входа в голос на этом устройстве: переподключение его не
    сбрасывает, выход – сбрасывает.
  */
  const voiceSince = useRef<number | null>(null);
  if (!inVoice) {
    voiceSince.current = null;
  } else if (voiceSince.current === null) {
    voiceSince.current = Date.now();
  }
  const voiceStartedAt = voiceSince.current;

  const content = useMemo<VoiceForegroundContent | null>(() => {
    /*
      Кнопка микрофона в шторке – короткими словами: значок микрофона рядом объясняет, о чём
      речь, а «Unmute microphone» система обрезала до «UNMUTE MICROPH…» (02.10.2026).
    */
    const micLabels = {
      muteLabel: t`post:voice.native.mute`,
      unmuteLabel: t`post:voice.native.unmute`,
    };

    if (inCall) {
      return {
        title: peerName ? t`Call with ${peerName}` : t`Call`,
        body: t`They can hear you while the app is minimized`,
        leaveLabel: t`End call`,
        name: peerName ?? t`Call`,
        // Пока собеседник не ответил, таймера нет: считать нечего.
        startedAt: callPhase === 'active' ? (callAcceptedAt ?? undefined) : undefined,
        micOn: callMicOn,
        ...micLabels,
      };
    }
    if (inVoice) {
      return {
        name: gameName ? t`In voice – ${gameName}` : t`In voice`,
        startedAt: voiceStartedAt ?? undefined,
        micOn: voiceMicOn,
        ...micLabels,
        title: gameName ? t`In voice – ${gameName}` : t`In voice`,
        // Без микрофона человека не слышно – табличка не обещает того, чего нет.
        body: micDenied
          ? t`post:voice.native.backgroundListenOnly`
          : t`Your squad can hear you while the app is minimized`,
        /*
          Тот же текст, но выбирает его нативная часть - по настоящему разрешению. `micDenied`
          приходит из браузерного запроса состояния, а он на Android 12 отвечает «granted» и про
          отозванное разрешение: человек без микрофона читал в шторке, что команда его слышит.
        */
        bodyListenOnly: t`post:voice.native.backgroundListenOnly`,
        leaveLabel: t`post:session.room.leaveVoice`,
      };
    }

    return null;
  }, [
    inCall,
    inVoice,
    peerName,
    gameName,
    micDenied,
    callPhase,
    callAcceptedAt,
    callMicOn,
    voiceStartedAt,
    voiceMicOn,
    t,
  ]);

  // Изначально «запущен»: после перезагрузки WebView сервис мог пережить старую страницу, а голос –
  // нет. Первый же проход без разговора снимает табличку, которая иначе врала бы «ты в голосе».
  const running = useRef(true);

  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }
    if (!content) {
      if (running.current) {
        running.current = false;
        void stopVoiceForeground();
      }
      return;
    }
    /*
      Первый вход в голос почти всегда упирается в отказ: сервис просим на «connecting», а
      системный вопрос про микрофон в этот момент ещё на экране, разрешения нет. Повторяем, когда
      приложение снова на переднем плане (диалог закрылся – страница видна) и на коротком таймере:
      из фона Android сервис не поднимет, пробовать там бессмысленно.
    */
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;

    const attempt = async () => {
      if (cancelled) {
        return;
      }
      /*
        Из фона сервис не запустить, но показанную табличку перерисовать можно: так кнопка
        микрофона в шторке меняется сразу после нажатия, а не после возврата в приложение.
      */
      if (document.visibilityState !== 'visible') {
        void updateVoiceForeground(content);
        return;
      }
      running.current = true;
      /*
        Без микрофона человек только слушает, и сервис защищает звук команды. Когда микрофон
        отказан явно (`micDenied`), такой защиты и достаточно. Иначе «только слушать» – это
        системный вопрос про микрофон, ещё висящий на экране: повторяем, пока разрешение не
        появится и сервис не переключится на микрофон.
      */
      const started = await startVoiceForeground(content, !micDenied);
      if (!started && !cancelled && attempts < RETRY_LIMIT) {
        attempts += 1;
        timer = setTimeout(() => void attempt(), RETRY_DELAY_MS);
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(timer);
        void attempt();
      }
    };

    void attempt();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [content, micDenied]);

  // Действие из шторки читает состояние в момент нажатия, а не из замыкания.
  const leaveRef = useRef<() => void>(() => undefined);
  leaveRef.current = () => {
    const phase = useCallStore.getState().phase;
    if (phase === 'outgoing' || phase === 'active') {
      hangup();
      return;
    }
    if (useVoiceStore.getState().phase !== 'idle') {
      void leaveVoice(queryClient);
      return;
    }
    // Разговора уже нет, а табличка осталась (например, после гибели WebView) – просто убрать её.
    void stopVoiceForeground();
  };

  // Кнопка микрофона в шторке: переключает тот разговор, который сейчас идёт.
  const toggleMicRef = useRef<() => void>(() => undefined);
  toggleMicRef.current = () => {
    const call = useCallStore.getState();
    if (call.phase === 'outgoing' || call.phase === 'active') {
      call.setMicEnabled(!call.micEnabled);
      return;
    }
    const voice = useVoiceStore.getState();
    if (voice.phase !== 'idle') {
      void requestVoiceMicrophone(!voice.micEnabled);
    }
  };

  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }
    let unsubscribe: (() => void) | undefined;
    let active = true;
    (async () => {
      const off = await onVoiceForegroundToggleMic(() => toggleMicRef.current());
      if (active) {
        unsubscribe = off;
      } else {
        off();
      }
    })();

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }
    let unsubscribe: (() => void) | undefined;
    let active = true;
    (async () => {
      const off = await onVoiceForegroundLeave(() => leaveRef.current());
      if (active) {
        unsubscribe = off;
      } else {
        off();
      }
    })();

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);
};
